import React, { useContext, useRef, useEffect } from 'react';
import ReactModal from 'react-modal-resizable-draggable';
import { PDFObject } from 'react-pdfobject';
import { MobXProviderContext, observer } from 'mobx-react';
import { register } from '../../../i18n';
import HtmlComponent from '../HtmlComponent';
import '../../../css/Tools.scss';
import '../../../css/Accessibility.scss';
import useReactModalResizeableDraggable from '../../../hooks/useReactModalResizeableDraggable';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import DialogPositionService from '../../services/DialogPositionService';
import accessibilityService from '../../services/AccessibilityService';

const t2 = register('AriaLabels');

const Help = observer(() => {
  const {
    toolbarManager
  } = useContext(MobXProviderContext);

  const {
    resourceType,
    helpData,
    helpDisplayName,
    helpOpenId
  } = toolbarManager;

  const closeRef = useRef();
  const contentRef = useRef();

  // Announce when help dialog opens and focus on content
  useEffect(() => {
    accessibilityService.announceMessage(`Help dialog opened: ${helpDisplayName}`);
    
    // Focus on the readable content area after a short delay to ensure the modal is rendered
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.focus();
      }
    }, 100);
  }, [helpDisplayName]);

  function closeHelp() {
    // Announce when help dialog closes
    accessibilityService.announceMessage(`Help dialog closed: ${helpDisplayName}`);
    
    toolbarManager.setIsHelpOpen(false);
    toolbarManager.toggleToolOff(helpOpenId);
  }

  let internals = null;
  if (resourceType === 'pdf_resource') {
    internals = (
      <div className="pdf-content-wrapper">
        <PDFObject url={helpData} />
      </div>
    );
  } else if (resourceType === 'article_resource') {
    internals = (
      <div className='article-container' data-allow='1'>
        <HtmlComponent htmlStr={helpData} />
      </div>
    );
  }

  useReactModalResizeableDraggable('helpTool');

  useAccessibilityClick(closeRef, closeHelp);

  return (
    <div className='helpTool'>
      <ReactModal
        initHeight={400}
        initWidth={800}
        isOpen={true}
        onRequestClose={closeHelp}
        top={DialogPositionService.getDefaultTopPosition()}
        role="dialog"
        aria-labelledby="help-dialog-title"
        aria-describedby="help-dialog-content">
        <div className='modalBody'>
          <div className='tool-header'>
            <h2 id="help-dialog-title" className='title-label basic'>{helpDisplayName}</h2>
            <div ref={closeRef} aria-label={t2('closedialog')} className='x-close-button' id='modal-close' role='button' tabIndex='0' />
          </div>
          <div 
            ref={contentRef}
            id="help-dialog-content" 
            className="help-content-scrollable"
            tabIndex='-1'
            role='document'
            aria-label={`${helpDisplayName} content`}>
            {internals}
          </div>
        </div>
      </ReactModal>
    </div>
  );
});

export default Help;
